import { useCallback, useState } from 'react';
import { useYodaCreateForm } from '../../yoda-ui/yodaForm';
import { SelectOption } from 'basics/options/options.types';
import useCepTemplateSelectorFiltersConfig from 'components/CepTemplateSelectorFilters/CepTemplateSelectorFilters.config';
import { SortFilter } from 'generated/maya.types';

type UseCepTemplateSelectorFiltersPropsType = {
  setCepTemplateSelectorFilters: (value: { channel?: string[] }) => void;
  setCepSortFilter: (value: SortFilter) => void;
};

const useCepTemplateSelectorFilters = ({
  setCepTemplateSelectorFilters,
  setCepSortFilter,
}: UseCepTemplateSelectorFiltersPropsType) => {
  const { cepTemplateSelectorFiltersConfig, cepSortFieldConfig } = useCepTemplateSelectorFiltersConfig();
  const { providerFields, setValue } = useYodaCreateForm();

  const [cepSortFilter, setLocalCepSortFilter] = useState(SortFilter.MOST_POPULAR);

  const [selectedFilters, setSelectedFilters] = useState<{
    channel: SelectOption[];
  }>({
    channel: [],
  });

  const hasSelectedFilters = selectedFilters.channel.length > 0;
  const hasActiveFilters = hasSelectedFilters;

  const handleChannelChange = useCallback((event: React.SyntheticEvent<Element, Event>, value: SelectOption[]) => {
    const channelValues = value.map((element) => element.id as string);
    setCepTemplateSelectorFilters({ channel: channelValues });
    setSelectedFilters((prev) => ({ ...prev, channel: value }));
  }, [setCepTemplateSelectorFilters]);

  const handleSortChange = useCallback((value: SelectOption) => {
    setLocalCepSortFilter(value.id as SortFilter);
    setCepSortFilter(value.id as SortFilter);
  }, [setCepSortFilter]);

  const handleRemoveChip = useCallback((filterType: 'channel', idToRemove: string | number) => {
    if (filterType === 'channel') {
      const newOptions = selectedFilters.channel.filter((o) => o.id !== idToRemove);
      const channelValues = newOptions.map((element) => element.id as string);
      setCepTemplateSelectorFilters({ channel: channelValues });
      setSelectedFilters((prev) => ({ ...prev, channel: newOptions }));
      setValue(cepTemplateSelectorFiltersConfig.channel.name, newOptions);
    }
  }, [selectedFilters.channel, setCepTemplateSelectorFilters, setValue, cepTemplateSelectorFiltersConfig.channel.name]);

  const handleReset = useCallback(() => {
    setSelectedFilters({
      channel: [],
    });
    setValue(cepTemplateSelectorFiltersConfig.channel.name, []);
    setCepTemplateSelectorFilters({});
  }, [cepTemplateSelectorFiltersConfig.channel.name, setSelectedFilters, setCepTemplateSelectorFilters, setValue]);

  return {
    cepTemplateSelectorFiltersConfig,
    cepSortFieldConfig,
    cepSortFilter,
    handleChannelChange,
    handleSortChange,
    providerFields,
    selectedFilters,
    handleRemoveChip,
    handleReset,
    hasSelectedFilters,
    hasActiveFilters,
  };
};

export default useCepTemplateSelectorFilters;
